import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, HostListener, Input, OnChanges, Output } from '@angular/core';

import { ImageProfile } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';

import { EpcotConfigService } from '../../common/epcot-config.service';
import { EventNameMapperService } from '../../event-details-common/event-name-mapper.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { PodType, TopEventsPodData } from '../top-events.model';

@Component({
    selector: 'ms-top-events-pod-header',
    templateUrl: 'top-events-pod-header.component.html',
    styleUrl: 'top-events-pod-header.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TopEventsPodHeaderComponent implements OnChanges {
    @HostBinding('class') className = 'pod-header';

    @Input() pod: TopEventsPodData;
    @Input() index: number;
    @Output() toggleExpansion = new EventEmitter();

    podType = PodType;
    title: string;
    sportId: number;
    leagueId: number;
    isSingleSport: boolean;
    isEpcotEnabled: boolean;
    isSingleCompetition: boolean;
    eventsCount: number;
    imageProfile: ImageProfile;

    constructor(
        private eventNameMapper: EventNameMapperService,
        private topEventsTrackingService: TopEventsTrackingService,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    ngOnChanges(): void {
        this.setModel();
    }

    @HostListener('click', ['$event'])
    toggleSelector(): void {
        this.toggleExpansion.emit();
        this.topEventsTrackingService.trackPodExpansion(this.pod, this.index, this.title);
    }

    get sportIcon(): string {
        return `icon sports-${this.sportId}`;
    }

    private setModel(): void {
        const firstEvent = this.pod.events[0];

        this.title = this.getTitle(firstEvent);
        this.sportId = firstEvent.sport.id;
        this.leagueId = firstEvent.league.realCompetitionId ?? firstEvent.league.parentLeagueId ?? firstEvent.league.id;
        this.isSingleSport = this.isSingleSportEvents(this.pod.events ?? []);
        this.isSingleCompetition = this.isSingleCompetitionEvents(this.pod.events ?? []);
        this.imageProfile = firstEvent.league?.imageProfile!;
    }

    private getTitle(event: EventModel): string {
        if (this.pod.title) {
            return this.pod.title;
        }

        switch (this.pod.podType) {
            case PodType.Single:
            case PodType.Outright:
                return this.eventNameMapper.map(event, true);
            case PodType.Grid:
                return event.league.name;
            case PodType.HandpickedOptionsPod:
            case PodType.BetBuilder:
                return this.pod.events?.length === 1 ? this.eventNameMapper.map(event, true) : '';
            default:
                return '';
        }
    }

    private isSingleCompetitionEvents(events: EventModel[]): boolean {
        return [...new Set(events.map((e) => e.league?.realCompetitionId ?? e.league?.parentLeagueId ?? e.league.id))].length === 1;
    }

    private isSingleSportEvents(events: EventModel[]): boolean {
        return [...new Set(events.map((event) => event.sport.id))].length === 1;
    }
}
